import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  DocumentIcon,
  TableCellsIcon,
  CloudArrowUpIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowPathIcon,
  CubeTransparentIcon,
  GlobeAltIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import './KnowledgeBase.css';

// ─── File type helpers ────────────────────────────────────────────────────

const FILE_ICONS = {
  pdf: '📄',
  docx: '📝',
  txt: '📃',
  md: '📋',
  csv: '📊',
  url: '🌐',
};

const FILE_COLORS = {
  pdf: 'type-pdf',
  docx: 'type-docx',
  txt: 'type-txt',
  md: 'type-md',
  csv: 'type-csv',
  url: 'type-url',
};

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// ─── Document row ─────────────────────────────────────────────────────────

function ChunkViewer({ chatbotId, documentId }) {
  const [chunks, setChunks] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [expanded, setExpanded] = React.useState(null); // chunk_index expanded

  const load = async () => {
    if (chunks) { setChunks(null); return; } // toggle off
    setLoading(true);
    try {
      const res = await axios.get(`/api/knowledge/${chatbotId}/${documentId}/chunks`);
      setChunks(res.data.chunks);
    } catch {
      toast.error('Failed to load chunks');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="chunk-viewer">
      <button className="btn btn-chunks btn-sm" onClick={load} title="Inspect how this document was split into chunks">
        <CubeTransparentIcon className="icon" />
        {loading ? 'Loading…' : chunks ? 'Hide Chunks' : 'View Chunks'}
      </button>

      {chunks && (
        <div className="chunk-list">
          <div className="chunk-list-header">
            {chunks.length} chunk{chunks.length !== 1 ? 's' : ''} —{' '}
            <span className="chunk-vec-count">
              {chunks.filter(c => c.has_embedding).length} vectorized
            </span>
            {chunks.some(c => !c.has_embedding) && (
              <span className="chunk-fts-count">
                , {chunks.filter(c => !c.has_embedding).length} text-only
              </span>
            )}
          </div>
          {chunks.map((c) => (
            <div key={c.chunk_index} className="chunk-item">
              <div
                className="chunk-item-header"
                onClick={() => setExpanded(expanded === c.chunk_index ? null : c.chunk_index)}
              >
                <span className="chunk-idx">#{c.chunk_index + 1}</span>
                <span className="chunk-chars">{c.char_count} chars</span>
                <span className={`chunk-badge ${c.has_embedding ? 'chunk-vec' : 'chunk-fts'}`}>
                  {c.has_embedding ? '🔢 Vector' : '📝 Text'}
                </span>
                <span className="chunk-preview">
                  {c.chunk_text.replace(/\n/g, ' ').slice(0, 80)}
                  {c.chunk_text.length > 80 ? '…' : ''}
                </span>
                {expanded === c.chunk_index
                  ? <ChevronUpIcon className="icon chunk-chevron" />
                  : <ChevronDownIcon className="icon chunk-chevron" />}
              </div>
              {expanded === c.chunk_index && (
                <pre className="chunk-full-text">{c.chunk_text}</pre>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DocumentRow({ doc, chatbotId, onDelete, onPreview, onReindex }) {
  const icon = FILE_ICONS[doc.file_type] || '📄';
  const colorClass = FILE_COLORS[doc.file_type] || 'type-txt';
  const isVectorized = doc.storage_method === 'pgvector';
  const [reindexing, setReindexing] = React.useState(false);

  const handleReindex = async () => {
    setReindexing(true);
    await onReindex(doc.document_id, doc.title || doc.file_name);
    setReindexing(false);
  };

  return (
    <div className="doc-row">
      <div className="doc-icon">{icon}</div>
      <div className="doc-info">
        <div className="doc-title" title={doc.title || doc.file_name}>
          {doc.title || doc.file_name}
        </div>
        {doc.file_name && doc.file_name !== doc.title && (
          <div className="doc-filename" title={doc.file_name}>
            {doc.file_type === 'url'
              ? (() => { try { const u = new URL(doc.file_name); return u.hostname + u.pathname; } catch { return doc.file_name; } })()
              : doc.file_name}
          </div>
        )}
        <div className="doc-meta">
          <span className={`badge ${colorClass}`}>{(doc.file_type || 'txt').toUpperCase()}</span>
          <span className="meta-sep">{formatBytes(doc.file_size)}</span>
          <span className="meta-sep">{doc.chunk_count} chunks</span>
          <span className={`badge storage-badge ${isVectorized ? 'vectorized' : 'fts-only'}`}>
            {isVectorized ? '🤖 Bot Trained' : '📝 Text Search'}
          </span>
          <span className="meta-sep">{formatDate(doc.created_at)}</span>
        </div>
        <ChunkViewer chatbotId={chatbotId} documentId={doc.document_id} />
      </div>
      <div className="doc-actions">
        <button
          className={`btn btn-reindex btn-sm ${isVectorized ? 'btn-reindex-refresh' : ''}`}
          onClick={handleReindex}
          disabled={reindexing}
          title={isVectorized ? 'Re-generate vector embeddings' : 'Generate vector embeddings'}
        >
          <ArrowPathIcon className={`icon ${reindexing ? 'spinning' : ''}`} />
          {reindexing ? 'Training…' : isVectorized ? 'Re-train Bot' : 'Train Bot'}
        </button>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => onPreview(doc.document_id)}
          title="Preview content"
        >
          Preview
        </button>
        <button
          className="btn btn-danger btn-sm"
          onClick={() => onDelete(doc.document_id, doc.title || doc.file_name)}
          title="Delete document"
        >
          <TrashIcon className="icon" />
        </button>
      </div>
    </div>
  );
}

const DOCS_PER_PAGE = 10;

// ─── Main component ───────────────────────────────────────────────────────

function KnowledgeBase() {
  const { id: chatbotId } = useParams();
  const navigate = useNavigate();

  const [chatbot, setChatbot] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [docFilter, setDocFilter] = useState('');
  const [removeAllOpen, setRemoveAllOpen] = useState(false);
  const [removeAllChecked, setRemoveAllChecked] = useState(false);
  const [removingAll, setRemovingAll] = useState(false);

  // Scheduled crawls
  const [schedules, setSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingName, setUploadingName] = useState('');
  const [uploadProgress, setUploadProgress] = useState(null); // { current, total }
  const cancelUploadRef = useRef(false);
  const [embeddingStatus, setEmbeddingStatus] = useState(null);

  const [previewDocId, setPreviewDocId] = useState(null);
  const [previewContent, setPreviewContent] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Scraper
  const [scrapeOpen, setScrapeOpen] = useState(false);
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [scrapeMode, setScrapeMode] = useState('single');
  const [scrapeMaxPages, setScrapeMaxPages] = useState(20);
  const [scrapeUnlimited, setScrapeUnlimited] = useState(false);
  const [scraping, setScraping] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchChatbot();
    fetchDocuments();
    fetchEmbeddingStatus();
    fetchSchedules();
  }, [chatbotId]);

  const fetchEmbeddingStatus = async () => {
    try {
      const res = await axios.get(`/api/knowledge/status`);
      setEmbeddingStatus(res.data);
    } catch {
      setEmbeddingStatus(null);
    }
  };

  const fetchSchedules = async () => {
    setLoadingSchedules(true);
    try {
      const res = await axios.get(`/api/knowledge/${chatbotId}/schedules`);
      setSchedules(res.data.schedules || []);
    } catch {
      /* non-fatal */
    } finally {
      setLoadingSchedules(false);
    }
  };

  const toggleSchedule = async (schedule) => {
    const updated = { ...schedule, is_active: !schedule.is_active };
    setSchedules(prev => prev.map(s => s.id === schedule.id ? updated : s));
    try {
      await axios.patch(`/api/knowledge/${chatbotId}/schedules/${schedule.id}`, {
        is_active: updated.is_active,
      });
    } catch {
      // revert on error
      setSchedules(prev => prev.map(s => s.id === schedule.id ? schedule : s));
      toast.error('Failed to update schedule');
    }
  };

  const deleteSchedule = async (schedule) => {
    if (!window.confirm(`Remove auto-crawl schedule for "${schedule.base_url}"?`)) return;
    try {
      await axios.delete(`/api/knowledge/${chatbotId}/schedules/${schedule.id}`);
      setSchedules(prev => prev.filter(s => s.id !== schedule.id));
      toast.success('Schedule removed');
    } catch {
      toast.error('Failed to remove schedule');
    }
  };

  const fetchChatbot = async () => {
    try {
      const res = await axios.get(`/api/chatbots/${chatbotId}`);
      setChatbot(res.data.chatbot);
    } catch {
      toast.error('Failed to load chatbot');
      navigate('/dashboard');
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoadingDocs(true);
      const res = await axios.get(`/api/knowledge/${chatbotId}`);
      setDocuments(res.data.documents);
      setCurrentPage(1);
      setDocFilter('');
    } catch {
      toast.error('Failed to load documents');
    } finally {
      setLoadingDocs(false);
    }
  };

  // ─── Upload (sequential queue) ───────────────────────────────────────────
  // Files are processed ONE AT A TIME to avoid hammering the server with
  // parallel requests. Progress is shown in the drop zone.

  const cancelUpload = () => { cancelUploadRef.current = true; };

  const handleFiles = useCallback(
    async (files) => {
      const fileArray = Array.from(files);
      if (!fileArray.length) return;

      cancelUploadRef.current = false;
      const total   = fileArray.length;
      const failed  = [];
      let succeeded = 0;

      setUploading(true);

      for (let i = 0; i < fileArray.length; i++) {
        if (cancelUploadRef.current) break;

        const file = fileArray[i];
        setUploadingName(file.name);
        setUploadProgress({ current: i + 1, total });

        const formData = new FormData();
        formData.append('file', file);

        try {
          await axios.post(`/api/knowledge/${chatbotId}/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          succeeded++;
        } catch (error) {
          const data = error.response?.data;
          if (data?.error === 'limit_exceeded') {
            toast.error(data.message, { duration: 7000 });
            break; // stop the whole queue on plan limits
          }
          failed.push(file.name);
        }
      }

      setUploading(false);
      setUploadingName('');
      setUploadProgress(null);
      cancelUploadRef.current = false;

      fetchDocuments();

      if (succeeded > 0 && !failed.length) {
        toast.success(`${succeeded} file${succeeded > 1 ? 's' : ''} uploaded successfully`);
      } else {
        if (succeeded > 0) toast.success(`${succeeded} file${succeeded > 1 ? 's' : ''} uploaded`);
        failed.forEach(name => toast.error(`Failed: ${name}`, { duration: 5000 }));
      }
    },
    [chatbotId, fetchDocuments]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) handleFiles(e.dataTransfer.files);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  // ─── Delete ─────────────────────────────────────────────────────────────

  const handleDelete = async (documentId, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`/api/knowledge/${chatbotId}/${documentId}`);
      toast.success('Document deleted');
      setDocuments((prev) => {
        const next = prev.filter((d) => d.document_id !== documentId);
        // If we deleted the last item on the current page, step back
        const maxPage = Math.max(1, Math.ceil(next.length / DOCS_PER_PAGE));
        setCurrentPage((p) => Math.min(p, maxPage));
        return next;
      });
      if (previewDocId === documentId) setPreviewDocId(null);
    } catch {
      toast.error('Failed to delete document');
    }
  };

  // ─── Delete all ──────────────────────────────────────────────────────────

  const handleDeleteAll = async () => {
    setRemovingAll(true);
    try {
      await axios.delete(`/api/knowledge/${chatbotId}`);
      toast.success('All documents removed');
      setDocuments([]);
      setCurrentPage(1);
      if (previewDocId) setPreviewDocId(null);
      setRemoveAllOpen(false);
      setRemoveAllChecked(false);
    } catch {
      toast.error('Failed to remove all documents');
    } finally {
      setRemovingAll(false);
    }
  };

  // ─── Reindex ─────────────────────────────────────────────────────────────

  const handleReindex = async (documentId, title) => {
    try {
      const res = await axios.post(`/api/knowledge/${chatbotId}/${documentId}/reindex`);
      if (res.data.storage_method === 'pgvector') {
        toast.success(`"${title}" vectorized — ${res.data.chunks} chunks stored`);
      } else {
        const reason = res.data.embedding_error || 'Unknown error';
        toast.error(`Vectorization failed: ${reason}`, { duration: 6000 });
      }
      setDocuments((prev) =>
        prev.map((d) =>
          d.document_id === documentId
            ? { ...d, storage_method: res.data.storage_method }
            : d
        )
      );
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to vectorize "${title}"`);
    }
  };

  // ─── Preview ─────────────────────────────────────────────────────────────

  const handlePreview = async (documentId) => {
    if (previewDocId === documentId) {
      setPreviewDocId(null);
      return;
    }
    setPreviewDocId(documentId);
    setLoadingPreview(true);
    try {
      const res = await axios.get(`/api/knowledge/${chatbotId}/${documentId}`);
      setPreviewContent(res.data.document.content || '(no content)');
    } catch {
      setPreviewContent('Failed to load preview.');
    } finally {
      setLoadingPreview(false);
    }
  };

  // ─── Scrape / Crawl URL ───────────────────────────────────────────────────
  const [crawlProgress, setCrawlProgress] = useState(null); // { visited, saved, currentUrl }

  const handleScrape = async (e) => {
    e.preventDefault();
    if (!scrapeUrl.trim()) return;
    setScraping(true);
    setCrawlProgress(null);

    if (scrapeMode === 'single') {
      // Simple POST → JSON
      try {
        const res = await axios.post(`/api/knowledge/${chatbotId}/scrape`, {
          url: scrapeUrl.trim(), mode: 'single',
        });
        toast.success(`Page scraped — ${res.data.pages[0]?.chunks ?? 0} chunks indexed`);
        setScrapeUrl(''); setScrapeOpen(false);
        fetchDocuments();
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to scrape page', { duration: 6000 });
      } finally {
        setScraping(false);
      }
      return;
    }

    // Crawl mode — fetch SSE stream with fetch() API
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    try {
      const response = await fetch(`/api/knowledge/${chatbotId}/scrape`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ url: scrapeUrl.trim(), mode: 'crawl', max_pages: scrapeMaxPages, unlimited: scrapeUnlimited }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${response.status}`);
      }

      const reader  = response.body.getReader();
      const decoder = new TextDecoder();
      let   buffer  = '';
      let   saved   = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // keep incomplete line

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));

            if (event.type === 'progress') {
              setCrawlProgress({
                visited: event.total_queued,
                currentUrl: event.url,
                saved,
                ok: event.ok,
              });
            } else if (event.type === 'page') {
              saved = event.saved;
              setCrawlProgress(p => ({ ...p, saved }));
            } else if (event.type === 'done') {
              toast.success(`Crawl complete — ${event.saved} page${event.saved !== 1 ? 's' : ''} indexed`);
              setScrapeUrl(''); setScrapeOpen(false); setCrawlProgress(null);
              fetchDocuments();
            } else if (event.type === 'error') {
              toast.error(event.message || 'Crawl failed', { duration: 6000 });
            }
          } catch { /* malformed JSON line — skip */ }
        }
      }
    } catch (err) {
      toast.error(err.message || 'Crawl failed', { duration: 6000 });
    } finally {
      setScraping(false);
      setCrawlProgress(null);
    }
  };

  // ─── RAG search ──────────────────────────────────────────────────────────

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setSearching(true);
    setSearchResults(null);
    try {
      const res = await axios.post(`/api/knowledge/${chatbotId}/search`, {
        query: searchQuery,
        top_k: 3,
      });
      setSearchResults(res.data.results);
    } catch {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="kb-page">
      {/* Header */}
      <header className="kb-header">
        <div className="container">
          <div className="header-content">
            <button onClick={() => navigate('/dashboard')} className="back-button">
              <ArrowLeftIcon className="icon" />
              Back
            </button>
            <div>
              <h1>Knowledge Base</h1>
              {chatbot && <p className="kb-subtitle">{chatbot.name}</p>}
            </div>
          </div>
        </div>
      </header>

      {embeddingStatus && (
        <div className={`embedding-banner ${embeddingStatus.embedding_available ? 'banner-ok' : 'banner-warn'}`}>
          {embeddingStatus.embedding_available
            ? '⚡ AI bot is ready to be trained. Add your documents below.'
            : '⚠️ Bot training unavailable — add HUGGINGFACE_API_KEY to enable AI training.'}
        </div>
      )}

      <main className="kb-main">
        <div className="container">
          <div className="kb-layout">
            {/* Left: Upload + Document list */}
            <div className="kb-left">
              {/* Drop zone */}
              <div
                className={`drop-zone ${dragOver ? 'drag-over' : ''} ${uploading ? 'uploading' : ''}`}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={() => setDragOver(false)}
                onClick={() => !uploading && fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.docx,.txt,.md,.csv"
                  style={{ display: 'none' }}
                  onChange={(e) => handleFiles(e.target.files)}
                />
                {uploading ? (
                  <>
                    <div className="upload-spinner" />
                    <p className="drop-title">
                      {uploadProgress && uploadProgress.total > 1
                        ? `Uploading ${uploadProgress.current} of ${uploadProgress.total}`
                        : 'Uploading…'}
                    </p>
                    <p className="drop-hint" style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {uploadingName}
                    </p>
                    {uploadProgress && uploadProgress.total > 1 && (
                      <div className="upload-progress-bar">
                        <div
                          className="upload-progress-fill"
                          style={{ width: `${Math.round((uploadProgress.current / uploadProgress.total) * 100)}%` }}
                        />
                      </div>
                    )}
                    {uploadProgress && uploadProgress.total > 1 && (
                      <button
                        type="button"
                        className="upload-cancel-btn"
                        onClick={e => { e.stopPropagation(); cancelUpload(); }}
                      >
                        Cancel remaining
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <CloudArrowUpIcon className="drop-icon" />
                    <p className="drop-title">Drag files here or click to upload</p>
                    <p className="drop-hint">PDF · DOCX · TXT · MD · CSV — up to 20 MB each</p>
                  </>
                )}
              </div>

              {/* Scrape URL panel */}
              <div className="scrape-panel">
                {!scrapeOpen ? (
                  <button className="btn-scrape" onClick={() => setScrapeOpen(true)}>
                    <GlobeAltIcon className="scrape-btn-icon" />
                    Add from URL
                  </button>
                ) : (
                  <form className="scrape-form" onSubmit={handleScrape}>
                    <div className="scrape-form-header">
                      <span><GlobeAltIcon className="scrape-btn-icon" /> Add from URL</span>
                      <button type="button" className="btn-icon-sm" onClick={() => setScrapeOpen(false)}>
                        <XMarkIcon style={{ width: 16, height: 16 }} />
                      </button>
                    </div>
                    <input
                      type="url"
                      className="scrape-input"
                      placeholder="https://example.com/about"
                      value={scrapeUrl}
                      onChange={e => setScrapeUrl(e.target.value)}
                      required
                      disabled={scraping}
                    />
                    <div className="scrape-options">
                      <label className={`scrape-mode ${scrapeMode === 'single' ? 'active' : ''}`}>
                        <input
                          type="radio"
                          name="scrapeMode"
                          value="single"
                          checked={scrapeMode === 'single'}
                          onChange={() => setScrapeMode('single')}
                        />
                        Single page
                      </label>
                      <label className={`scrape-mode ${scrapeMode === 'crawl' ? 'active' : ''}`}>
                        <input
                          type="radio"
                          name="scrapeMode"
                          value="crawl"
                          checked={scrapeMode === 'crawl'}
                          onChange={() => setScrapeMode('crawl')}
                        />
                        Crawl site
                      </label>
                      {scrapeMode === 'crawl' && (
                        <label className="scrape-unlimited">
                          <input
                            type="checkbox"
                            checked={scrapeUnlimited}
                            onChange={e => setScrapeUnlimited(e.target.checked)}
                          />
                          Import all pages found
                        </label>
                      )}
                      {scrapeMode === 'crawl' && !scrapeUnlimited && (
                        <label className="scrape-maxpages">
                          Max pages:
                          <input
                            type="number"
                            min={1}
                            max={50}
                            value={scrapeMaxPages}
                            onChange={e => setScrapeMaxPages(parseInt(e.target.value, 10))}
                            className="scrape-maxpages-input"
                          />
                        </label>
                      )}
                    </div>
                    <button
                      type="submit"
                      className="btn-scrape-submit"
                      disabled={scraping || !scrapeUrl.trim()}
                    >
                      {scraping
                        ? (scrapeMode === 'crawl' ? `Crawling…` : 'Scraping…')
                        : (scrapeMode === 'crawl' ? `Crawl & Import` : 'Scrape & Import')}
                    </button>
                    {scraping && scrapeMode === 'crawl' && (
                      <div className="crawl-progress">
                        <div className="crawl-progress-bar">
                          <div
                            className="crawl-progress-fill"
                            style={{ width: crawlProgress && !scrapeUnlimited ? `${Math.min(100, (crawlProgress.saved / scrapeMaxPages) * 100)}%` : '40%' }}
                          />
                        </div>
                        <div className="crawl-progress-info">
                          {crawlProgress ? (
                            <>
                              <span>✅ {crawlProgress.saved} saved · 🔍 {crawlProgress.visited} found</span>
                              <span className="crawl-current-url" title={crawlProgress.currentUrl}>
                                {crawlProgress.currentUrl?.replace(/^https?:\/\//, '').slice(0, 45)}…
                              </span>
                            </>
                          ) : (
                            <span>Starting crawl…</span>
                          )}
                        </div>
                      </div>
                    )}
                  </form>
                )}
              </div>

              {/* Document list */}
              <div className="docs-section">
                <div className="docs-header">
                  <h2>
                    Documents
                    <span className="doc-count">{documents.length}</span>
                  </h2>
                  {documents.length > 0 && (
                    <button
                      className="btn btn-danger btn-sm btn-remove-all"
                      onClick={() => { setRemoveAllChecked(false); setRemoveAllOpen(true); }}
                      title="Remove all documents and start fresh"
                    >
                      <TrashIcon className="icon" />
                      Remove All
                    </button>
                  )}
                </div>

                {documents.length > 0 && (
                  <div className="docs-search-bar">
                    <MagnifyingGlassIcon className="docs-search-icon" />
                    <input
                      type="text"
                      className="docs-search-input"
                      placeholder="Filter documents…"
                      value={docFilter}
                      onChange={e => { setDocFilter(e.target.value); setCurrentPage(1); }}
                    />
                    {docFilter && (
                      <button className="docs-search-clear" onClick={() => { setDocFilter(''); setCurrentPage(1); }}>
                        <XMarkIcon style={{ width: 14, height: 14 }} />
                      </button>
                    )}
                  </div>
                )}

                {loadingDocs ? (
                  <div className="loading-docs">Loading documents…</div>
                ) : documents.length === 0 ? (
                  <div className="empty-docs">
                    <DocumentIcon className="empty-icon" />
                    <p>No documents yet</p>
                    <small>Upload files above to give your chatbot knowledge</small>
                  </div>
                ) : (() => {
                  const q = docFilter.trim().toLowerCase();
                  const filtered = q
                    ? documents.filter(d =>
                        (d.title || '').toLowerCase().includes(q) ||
                        (d.file_name || '').toLowerCase().includes(q)
                      )
                    : documents;

                  const totalPages = Math.max(1, Math.ceil(filtered.length / DOCS_PER_PAGE));
                  const safePage   = Math.min(currentPage, totalPages);
                  const pageStart  = (safePage - 1) * DOCS_PER_PAGE;
                  const pageDocs   = filtered.slice(pageStart, pageStart + DOCS_PER_PAGE);

                  return (
                    <>
                      {filtered.length === 0 && (
                        <div className="empty-docs">
                          <MagnifyingGlassIcon className="empty-icon" />
                          <p>No documents match "{docFilter}"</p>
                        </div>
                      )}
                      <div className="doc-list">
                        {pageDocs.map((doc) => (
                          <React.Fragment key={doc.document_id}>
                            <DocumentRow
                              doc={doc}
                              chatbotId={chatbotId}
                              onDelete={handleDelete}
                              onPreview={handlePreview}
                              onReindex={handleReindex}
                            />
                            {previewDocId === doc.document_id && (
                              <div className="doc-preview">
                                {loadingPreview ? (
                                  <p className="preview-loading">Loading…</p>
                                ) : (
                                  <pre className="preview-text">{previewContent.slice(0, 2000)}{previewContent.length > 2000 ? '\n\n… (truncated)' : ''}</pre>
                                )}
                              </div>
                            )}
                          </React.Fragment>
                        ))}
                      </div>

                      {totalPages > 1 && (
                        <div className="kb-pagination">
                          <button
                            className="kb-page-btn"
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                            disabled={safePage === 1}
                          >
                            ‹ Prev
                          </button>

                          {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                            <button
                              key={n}
                              className={`kb-page-btn ${n === safePage ? 'kb-page-active' : ''}`}
                              onClick={() => setCurrentPage(n)}
                            >
                              {n}
                            </button>
                          ))}

                          <button
                            className="kb-page-btn"
                            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                            disabled={safePage === totalPages}
                          >
                            Next ›
                          </button>

                          <span className="kb-page-info">
                            {pageStart + 1}–{Math.min(pageStart + DOCS_PER_PAGE, filtered.length)} of {filtered.length}{q ? ` (filtered)` : ''}
                          </span>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Right: RAG search tester */}
            <div className="kb-right">
              <div className="search-panel">
                <button
                  className="search-toggle"
                  onClick={() => setSearchOpen((o) => !o)}
                >
                  <MagnifyingGlassIcon className="icon" />
                  Test RAG Search
                  {searchOpen ? (
                    <ChevronUpIcon className="icon chevron" />
                  ) : (
                    <ChevronDownIcon className="icon chevron" />
                  )}
                </button>

                {searchOpen && (
                  <div className="search-body">
                    <p className="search-hint">
                      Simulate what context the chatbot retrieves for a given question.
                    </p>
                    <form onSubmit={handleSearch} className="search-form">
                      <input
                        type="text"
                        className="search-input"
                        placeholder="Ask something…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={searching || !searchQuery.trim()}
                      >
                        {searching ? 'Searching…' : 'Search'}
                      </button>
                    </form>

                    {searchResults !== null && (
                      <div className="search-results">
                        {searchResults.length === 0 ? (
                          <p className="no-results">No matching context found.</p>
                        ) : (
                          searchResults.map((r, i) => (
                            <div key={i} className="result-card">
                              <div className="result-header">
                                <span className="result-num">#{i + 1}</span>
                                {r.title && <span className="result-title">{r.title}</span>}
                                <span className="result-score">
                                  score: {typeof r.score === 'number' ? r.score.toFixed(4) : r.score}
                                </span>
                              </div>
                              <p className="result-text">{r.text}</p>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Scheduled crawls */}
              {(schedules.length > 0 || loadingSchedules) && (
                <div className="schedule-panel">
                  <h3>
                    <ArrowPathIcon className="icon" style={{ width: 14, height: 14, marginRight: 4, verticalAlign: 'middle' }} />
                    Auto-Crawl Schedules
                  </h3>
                  {loadingSchedules ? (
                    <p className="schedule-loading">Loading…</p>
                  ) : (
                    <div className="schedule-list">
                      {schedules.map(s => (
                        <div key={s.id} className={`schedule-row ${s.is_active ? '' : 'schedule-inactive'}`}>
                          <div className="schedule-info">
                            <div className="schedule-url" title={s.base_url}>
                              {(() => { try { return new URL(s.base_url).hostname; } catch { return s.base_url; } })()}
                            </div>
                            <div className="schedule-meta">
                              {s.last_crawled_at
                                ? <>Last run {formatDate(s.last_crawled_at)} · {s.last_pages_updated} updated</>
                                : 'Not yet run'}
                              {' · '}{s.unlimited ? 'unlimited' : `max ${s.max_pages}`}
                            </div>
                          </div>
                          <div className="schedule-actions">
                            <button
                              className={`schedule-toggle ${s.is_active ? 'toggle-on' : 'toggle-off'}`}
                              onClick={() => toggleSchedule(s)}
                              title={s.is_active ? 'Disable auto-crawl' : 'Enable auto-crawl'}
                            >
                              {s.is_active ? '⏸ Active' : '▶ Paused'}
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => deleteSchedule(s)}
                              title="Remove schedule"
                            >
                              <TrashIcon className="icon" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="schedule-hint">
                    Each active schedule re-crawls every 24 h and only re-indexes pages that are new or changed.
                  </p>
                </div>
              )}

              {/* Quick stats */}
              <div className="stats-panel">
                <h3>Storage</h3>
                <div className="stat-row">
                  <span>Total documents</span>
                  <strong>{documents.length}</strong>
                </div>
                <div className="stat-row">
                  <span>Total chunks</span>
                  <strong>{documents.reduce((a, d) => a + (d.chunk_count || 0), 0)}</strong>
                </div>
                <div className="stat-row">
                  <span>🤖 Bot Trained</span>
                  <strong>{documents.filter((d) => d.storage_method === 'pgvector').length}</strong>
                </div>
                <div className="stat-row">
                  <span>📝 Text search only</span>
                  <strong>{documents.filter((d) => d.storage_method !== 'pgvector').length}</strong>
                </div>
                <div className="stat-row">
                  <span>Total size</span>
                  <strong>{formatBytes(documents.reduce((a, d) => a + (d.file_size || 0), 0))}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Remove All confirmation modal */}
      {removeAllOpen && (
        <div className="modal-overlay" onClick={() => !removingAll && setRemoveAllOpen(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-icon-wrap modal-icon-danger">
              <TrashIcon className="modal-icon" />
            </div>
            <h3 className="modal-title">Remove all documents?</h3>
            <p className="modal-body">
              This will permanently delete all <strong>{documents.length} document{documents.length !== 1 ? 's' : ''}</strong> and
              their chunks from this knowledge base. The chatbot will have no knowledge until you add new documents.
            </p>
            <label className="modal-check-label">
              <input
                type="checkbox"
                className="modal-checkbox"
                checked={removeAllChecked}
                onChange={e => setRemoveAllChecked(e.target.checked)}
                disabled={removingAll}
              />
              I understand this action is irreversible
            </label>
            <div className="modal-actions">
              <button
                className="btn btn-ghost"
                onClick={() => setRemoveAllOpen(false)}
                disabled={removingAll}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteAll}
                disabled={!removeAllChecked || removingAll}
              >
                {removingAll ? 'Removing…' : 'Yes, remove all'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default KnowledgeBase;
